
import { useEffect, useRef } from 'react';
import {
  useAuthenticated,
  useAuthtoken,
  useSelectedEntityId,
  useSelectedOrganizationId,
  useTenantScopeRevision,
  useTenantSwitchPending,
  commitTenantScope,
  setAuthToken,
  setSelectedEntityId,
  setSelectedOrganizationId,
  setTenantSwitchPending,
  logout,
} from 'stores/useAuthStore';
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';

import request from 'utils/request';
import countries from 'utils/countries';
import { showSnackbar } from 'stores/useUiStore';
import { setUser, markUserUpdated, useUser } from 'stores/useSessionStore';
import { useOrganizationsList } from 'stores/useOrganizationsStore';
import { resetTenantScopedState } from 'hooks/useTenantScopeReset';
import { TOKEN_INVALID } from 'containers/Base/useBaseViewData';
import type { ApiRespBody } from 'types/api';
import type { User } from '../user.types';

function defaultCountry(): string {
  const timezone = moment.tz.guess();
  let abbr = 'US';
  (countries as { abbr: string; zones: string[] }[]).forEach((country) => {
    if (country.zones.indexOf(timezone) !== -1) abbr = country.abbr;
  });
  return abbr;
}

export const CURRENT_USER_QUERY_KEY = ['currentUser'] as const;

type CurrentUserResponse = Omit<ApiRespBody<User>, 'message'> & {
  message: User;
};

export async function fetchCurrentUser(
  authToken: string
): Promise<CurrentUserResponse | null> {
  return request<CurrentUserResponse>('/api/users/user', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', authorization: authToken },
  });
}

export function currentUserQueryOptions(authToken: string) {
  return queryOptions({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: () => fetchCurrentUser(authToken),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

export function buildUserUpdatePayload(
  user: User,
  selectedEntityId: User['selectedEntityId'],
  selectedOrganizationId: User['selectedOrganizationId']
): User {
  return {
    ...user,
    country: user.country || defaultCountry(),
    selectedEntityId,
    selectedOrganizationId,
    timezone: user.timezone || moment.tz.guess(),
  };
}

export async function updateUserProfile(
  payload: User,
  authToken: string
): Promise<ApiRespBody | null> {
  return request<ApiRespBody>(`/api/users/${payload.userName}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', authorization: authToken },
  });
}

function persistSignature(
  user: User,
  selectedEntityId: User['selectedEntityId'],
  selectedOrganizationId: User['selectedOrganizationId']
): string {
  const payload = buildUserUpdatePayload(
    user,
    selectedEntityId,
    selectedOrganizationId
  );
  payload.lastUpdated = undefined;
  return JSON.stringify(payload);
}

interface PersistVars {
  user: User;
  selectedEntityId: User['selectedEntityId'];
  selectedOrganizationId: User['selectedOrganizationId'];
}

export function useOrganizationSelectionMutation() {
  const authToken = useAuthtoken();
  const selectedEntityId = useSelectedEntityId();
  const user = useUser();

  return useMutation({
    mutationFn: async (selectedOrganizationId: number) => {
      setTenantSwitchPending(true);
      const result = await updateUserProfile(
        buildUserUpdatePayload(
          user,
          selectedEntityId,
          selectedOrganizationId
        ),
        authToken
      );
      return { result, selectedOrganizationId };
    },
    onSuccess: ({ result, selectedOrganizationId }) => {
      if (result?.success && result.token) {
        resetTenantScopedState();
        markUserUpdated({ selectedEntityId, selectedOrganizationId });
        commitTenantScope({
          authtoken: result.token,
          selectedEntityId,
          selectedOrganizationId,
        });
        return;
      }

      setTenantSwitchPending(false);
      showSnackbar({
        autoHideDuration: 5000,
        caller: 'onUserUpdate',
        open: true,
        message:
          result?.message ?? 'Failed to switch the selected organization',
        severity: 'error',
      });
    },
    onError: (error) => {
      setTenantSwitchPending(false);
      console.error(error);
    },
  });
}

export function useUserAccountSync(): void {
  const authenticated = useAuthenticated();
  const authToken = useAuthtoken();
  const selectedEntityId = useSelectedEntityId();
  const selectedOrganizationId = useSelectedOrganizationId();
  const user = useUser();
  const orgs = useOrganizationsList();
  const tenantSwitchPending = useTenantSwitchPending();
  const tenantScopeRevision = useTenantScopeRevision();

  const lastSyncedSigRef = useRef<string | null>(null);
  const tenantScopeRevisionRef = useRef(tenantScopeRevision);

  const selectedEntityIdRef = useRef(selectedEntityId);
  selectedEntityIdRef.current = selectedEntityId;
  const selectedOrganizationIdRef = useRef(selectedOrganizationId);
  selectedOrganizationIdRef.current = selectedOrganizationId;

  const userQuery = useQuery({
    ...currentUserQueryOptions(authToken),
    enabled: authenticated && !!authToken,
  });

  useEffect(() => {
    if (!(userQuery.error instanceof Error)) return;
    if (userQuery.error.message === TOKEN_INVALID) {
      logout();
      return;
    }
    showSnackbar({
      autoHideDuration: null,
      caller: 'onUserGet',
      open: true,
      message: userQuery.error.message,
      severity: 'error',
    });
  }, [userQuery.error]);

  const queryData = userQuery.data;
  useEffect(() => {
    if (!queryData) return;
    if (!queryData.success) {
      logout();
      return;
    }
    // Legacy contract: the user object arrives in `message`.
    const fetchedUser = queryData.message;
    if (queryData.token) setAuthToken(queryData.token);
    setUser(fetchedUser);
    if (selectedEntityIdRef.current !== fetchedUser.selectedEntityId) {
      setSelectedEntityId(fetchedUser.selectedEntityId);
    }
    if (selectedOrganizationIdRef.current !== fetchedUser.selectedOrganizationId) {
      setSelectedOrganizationId(fetchedUser.selectedOrganizationId);
    }
    lastSyncedSigRef.current = persistSignature(
      fetchedUser,
      fetchedUser.selectedEntityId,
      fetchedUser.selectedOrganizationId
    );
  }, [queryData]);

  const { mutate: persistUser } = useMutation({
    mutationFn: (vars: PersistVars) =>
      updateUserProfile(
        buildUserUpdatePayload(
          vars.user,
          vars.selectedEntityId,
          vars.selectedOrganizationId
        ),
        authToken
      ),
    onSuccess: (result, vars) => {
      if (result?.success) {
        if (result.token) setAuthToken(result.token);
        markUserUpdated({
          selectedEntityId: vars.selectedEntityId,
          selectedOrganizationId: vars.selectedOrganizationId,
        });
      } else {
        showSnackbar({
            autoHideDuration: 5000,
            caller: 'onUserUpdate',
            open: true,
            message: result?.message ?? 'Failed to update user profile',
            severity: 'error',
          });
        if (vars.selectedEntityId !== vars.user.selectedEntityId) {
          setSelectedEntityId(vars.user.selectedEntityId);
        }
        if (vars.selectedOrganizationId !== vars.user.selectedOrganizationId) {
          setSelectedOrganizationId(vars.user.selectedOrganizationId);
        }
      }
    },
    onError: (err) => console.error(err),
  });

  useEffect(() => {
    if (!authenticated) return;

    const sig = persistSignature(user, selectedEntityId, selectedOrganizationId);

    if (tenantScopeRevisionRef.current !== tenantScopeRevision) {
      tenantScopeRevisionRef.current = tenantScopeRevision;
      lastSyncedSigRef.current = sig;
      return;
    }

    if (lastSyncedSigRef.current === null) {
      lastSyncedSigRef.current = sig;
      return;
    }
    if (sig === lastSyncedSigRef.current) return;
    if (tenantSwitchPending) return;

    const isEntityWithNoOrgs = !!orgs.lastUpdated && orgs.values.length === 0;
    if (!(selectedEntityId && (selectedOrganizationId || isEntityWithNoOrgs))) {
      return;
    }

    lastSyncedSigRef.current = sig;
    persistUser({ user, selectedEntityId, selectedOrganizationId });
  }, [
    authenticated,
    user,
    selectedEntityId,
    selectedOrganizationId,
    tenantScopeRevision,
    tenantSwitchPending,
    orgs,
    persistUser,
  ]);
}
